/**
 * effectiveRole — runtime accessor for the current viewer's role on
 * a given board.
 *
 * FOR-173: replaced the previous "always editor" stub. The backend
 * boardWire response now carries an `effective_role` field resolved
 * per-request via auth.PermissionResolver.EffectiveRole. The REST
 * client (`api/boardsClient.ts`) calls `setEffectiveRole(boardId,
 * role)` whenever it observes the field on a GET / list / create
 * response; this module stores the value in a process-local Map and
 * returns it from `getEffectiveRole(boardId)`.
 *
 * Fallback when nothing is cached: returns 'editor'. Reason — the
 * backend omits the field when the resolver returns an empty string
 * (degraded / unauthenticated edge), and historically the SPA
 * assumed editor; keeping that default avoids silently locking out
 * existing users on the first request after rollout. A viewer with
 * an explicit cached role is correctly gated.
 */

import type { ElementId } from '../sync/types.js';

export type EffectiveRole = 'owner' | 'editor' | 'commenter' | 'viewer';

const cache = new Map<string, EffectiveRole>();

export function setEffectiveRole(boardId: string | undefined, role: EffectiveRole | undefined): void {
  if (!boardId) return;
  if (role === undefined) {
    cache.delete(boardId);
    return;
  }
  cache.set(boardId, role);
}

export function getEffectiveRole(boardId: ElementId | string | undefined): EffectiveRole {
  if (!boardId) return 'editor';
  return cache.get(boardId) ?? 'editor';
}

export function getResolvedEffectiveRole(boardId: ElementId | string | undefined): EffectiveRole | undefined {
  return boardId ? cache.get(boardId) : undefined;
}

// Document mutations remain restricted to editing roles.
export function canWrite(role: EffectiveRole | undefined): boolean {
  return role === 'owner' || role === 'editor';
}

// Comment permission is independent of document mutation permission.
export function canComment(role: EffectiveRole | undefined): boolean {
  return role === 'owner' || role === 'editor' || role === 'commenter';
}

/** Export is a read action for every explicitly resolved role. */
export function canExport(role: EffectiveRole | undefined): boolean {
  return role === 'owner' || role === 'editor' || role === 'commenter' || role === 'viewer';
}

// Test helper. Not exported from package barrel.
export function __resetEffectiveRoleCacheForTests(): void {
  cache.clear();
}
